import React, { useCallback } from 'react';
import Autolinker from 'autolinker';
import parse from 'html-react-parser';

import { get, isEmpty } from 'lodash';
import {
  bool, func, number, oneOfType, shape, string,
} from 'prop-types';
import SingleQuestion from './single-question-type';
import MultipleQuestionType from './multiple-question-type';
import RangeQuestion from './range-question-type';
import WrittenQuestion from './written-question-type';
import TextQuestion from './text-question-type';
import PreferenceQuestionType from './preference-question-type';
import FirstClickTestType from './first-click-test-type';
import ImageQuestionType from './image-question-type';
import RankingQuestionType from './ranking-question-type';

import MatrixQuestionType from './matrix-question-type';
import FiveSecondQuestionType from './five-second-question-type';
/**
 * Use this object to select the component type that will be used.
 */
export const TYPE_QUESTIONS = {
  SINGLE_SELECT: 'single_select',
  MULTIPLE_SELECT: 'multiple_select',
  RANGE: 'range',
  WRITTEN: 'written',
  TEXT_QUESTION: 'task',
  IMAGE_SELECT: 'image_select',
  PREFERENCE: 'preference',
  FIRST_CLICK_TEST: 'first_click_test',
  RANKING: 'ranking',
  MATRIX: 'matrix',
  FIVE_SECOND: 'five_second',
};

/**
 * Used to organize the components in a single place.
 * @param {object} props Values to pass to the component that will be returned.
 * @returns The component seleted by the variable 'typeQuestion'.
 */
export const QuestionsComponents = (props) => {
  const {
    typeQuestion,
    question,
    placeholder,
    onChange,
    selectedValue,
    errorField,
    index,
    projectType,
    isScreener,
    setHideBottomButton,
    submitQuestions,
    onGoToNextPage,
  } = props;

  const questionOptions = get(question, 'questionOptions', []);
  const label = get(question, 'label', '');
  const leftLabel = get(question, 'leftLabel', '');
  const rightLabel = get(question, 'rightLabel', '');
  const imageUrl = get(question, 'imageUrl', null);
  const displayTime = get(question, 'displayTime', 5);
  const matrixType = get(question, 'matrixType', 'single_select');
  const multipleSelectionArray = get(question, 'multipleSelectionArray', []);

  const checkProjectType = useCallback((testType) => {
    if (isEmpty(selectedValue)) {
      return '';
    }

    if (testType === 'survey') {
      return selectedValue;
    }

    return selectedValue.value;
  }, [selectedValue]);

  const currentSelectedValues = checkProjectType(projectType);
  const linkedLabel = parse(Autolinker.link(label));

  if (typeQuestion === TYPE_QUESTIONS.FIRST_CLICK_TEST) {
    setHideBottomButton((prev) => (prev === false ? true : prev));
  } else {
    setHideBottomButton((prev) => (prev === true ? false : prev));
  }

  switch (typeQuestion) {
    case TYPE_QUESTIONS.WRITTEN: {
      return (
        <WrittenQuestion
          placeholder={placeholder}
          onChange={onChange}
          selectedValue={currentSelectedValues}
          label={linkedLabel}
          errorField={errorField}
          index={index}
        />
      );
    }
    case TYPE_QUESTIONS.RANGE: {
      return (
        <RangeQuestion
          questionOptions={questionOptions}
          label={linkedLabel}
          onChange={onChange}
          selectedValue={currentSelectedValues}
          leftLabel={leftLabel}
          rightLabel={rightLabel}
          errorField={errorField}
          index={index}
        />
      );
    }
    case TYPE_QUESTIONS.MULTIPLE_SELECT: {
      return (
        <MultipleQuestionType
          questionOptions={questionOptions}
          label={linkedLabel}
          onChange={onChange}
          selectedValue={currentSelectedValues}
          errorField={errorField}
          index={index}
          isScreener={isScreener}
          multipleSelectionArray={multipleSelectionArray}
        />
      );
    }
    case TYPE_QUESTIONS.SINGLE_SELECT: {
      return (
        <SingleQuestion
          questionOptions={questionOptions}
          label={linkedLabel}
          onChange={onChange}
          selectedValue={currentSelectedValues}
          errorField={errorField}
          index={index}
          isScreener={isScreener}
        />
      );
    }
    case TYPE_QUESTIONS.TEXT_QUESTION: {
      return (
        <TextQuestion
          label={linkedLabel}
          index={index}
        />
      );
    }
    case TYPE_QUESTIONS.IMAGE_SELECT: {
      return (
        <ImageQuestionType
          label={linkedLabel}
          index={index}
          imageUrl={imageUrl}
        />
      );
    }
    case TYPE_QUESTIONS.PREFERENCE: {
      return (
        <PreferenceQuestionType
          label={linkedLabel}
          index={index}
          onChange={onChange}
          selectedValue={currentSelectedValues}
          errorField={!!errorField}
          questionOptions={questionOptions}
        />
      );
    } case TYPE_QUESTIONS.MATRIX: {
      return (
        <MatrixQuestionType
          label={linkedLabel}
          index={index}
          onChange={onChange}
          selectedValue={currentSelectedValues}
          errorField={!!errorField}
          questionOptions={questionOptions}
          matrixType={matrixType}
        />
      );
    }
    case TYPE_QUESTIONS.FIVE_SECOND: {
      return (
        <FiveSecondQuestionType
          label={linkedLabel}
          displayTime={displayTime}
          src={imageUrl}
          submitQuestions={submitQuestions}
        />
      );
    }
    case TYPE_QUESTIONS.FIRST_CLICK_TEST: {
      return (
        <FirstClickTestType
          label={linkedLabel}
          index={index}
          imageUrl={imageUrl}
          selectedValue={currentSelectedValues}
          onChange={onChange}
          errorField={errorField}
          onGoToNextPage={onGoToNextPage}
        />
      );
    }
    case TYPE_QUESTIONS.RANKING: {
      return (
        <RankingQuestionType
          questionOptions={questionOptions}
          label={linkedLabel}
          onChange={onChange}
          errorField={errorField}
          index={index}
        />
      );
    }
    default:
      return null;
  }
};

QuestionsComponents.propTypes = {
  projectType: string,
  typeQuestion: string,
  question: shape({
    id: string,
  }),
  placeholder: string,
  onChange: func,
  selectedValue: oneOfType([
    string,
    shape({}),
  ]),
  errorField: bool,
  index: number,
  isScreener: bool,
  setHideBottomButton: func,
  submitQuestions: func,
  onGoToNextPage: func,
};

QuestionsComponents.defaultProps = {
  projectType: '',
  typeQuestion: '',
  placeholder: 'Type your answer',
  question: {},
  onChange: () => { },
  selectedValue: null,
  errorField: false,
  index: null,
  isScreener: false,
  setHideBottomButton: () => { },
  submitQuestions: () => { },
  onGoToNextPage: () => { },
};
